import { AxiosRequestConfig } from "axios";

import { Override } from "../types";
import { ThinPerson } from "./People";
import { factory } from "./client";

const client = factory("users");

export const LOGIN_BASE_URI = process.env.REACT_APP_LOGIN_BASE_URI || "/oauth/login";
export const LOGOUT_URI = process.env.REACT_APP_LOGOUT_URI || "/logout";

export enum PersonalSyncTransparency {
  Default = "DEFAULT",
  Private = "PRIVATE",
  SemiPrivate = "SEMI_PRIVATE",
  SemiPrivateAlt = "SEMI_PRIVATE_ALT",
  Public = "PUBLIC",
}

export enum DayOfWeek {
  Monday = "MONDAY",
  Tuesday = "TUESDAY",
  Wednesday = "WEDNESDAY",
  Thursday = "THURSDAY",
  Friday = "FRIDAY",
  Saturday = "SATURDAY",
  Sunday = "SUNDAY",
}

export const Weekdays = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
];

export type AssistPolicy = {
  enabled: boolean;
  notification: boolean;
  windowStart: string;
  windowEnd: string;
  idealTime: string;
  durationMin: number;
  durationMax: number;
};

export type AssistSettings = {
  readonly enabled: boolean;
  focus: boolean;
  travel: boolean;
};

export type WorkingHours = {
  days: DayOfWeek[];
  start: string;
  end: string;
};

export type UserSettings = {
  advancedSyncControl: boolean;
  modifyCalendar: boolean;
  scheduler: number;
  assistSettings: AssistSettings;
  tasksEnabled: boolean;
  personalSyncWorkingHoursOnly: boolean;
  personalSyncTransparency: PersonalSyncTransparency;
  workingHours: WorkingHours;
};

export type UserFeature = keyof UserSettings;
export type UserFeatureFlag = {
  modifyCalendar: boolean;
  assist: boolean;
  personalSyncWorkingHoursOnly: boolean;
  "assistSettings.focus": boolean;
  "assistSettings.travel": boolean;
};

export type User = {
  readonly id: string;
  readonly principal: string;
  readonly provider: string;
  readonly email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  timezone: string;
  readonly admin?: boolean;
  readonly impersonated?: boolean;
  readonly syncUser?: boolean;
  readonly slackEnabled?: boolean;
  readonly likelyPersonal?: boolean;
  features: UserSettings;
  readonly trackingCode: string;
  readonly refCode: string;
  locale?: string;
  onboarded?: boolean;
};

export type PatchUser = Partial<
  Override<
    Partial<User>,
    {
      features: Partial<UserSettings>;
    }
  >
>;

export function getCurrentUser(config?: AxiosRequestConfig) {
  return client.get<User>("/current", config);
}

export function updateCurrentUser(user: User, config?: AxiosRequestConfig) {
  return client.put<User>("/current", user, config);
}

export function patchCurrentUser(user: PatchUser, config?: AxiosRequestConfig) {
  return client.patch<User>("/current", user, config);
}

export function deleteCurrentUser(config?: AxiosRequestConfig) {
  return client.delete<null>("/current", config);
}

export function getCurrentUserContacts(config?: AxiosRequestConfig) {
  return client.get<ThinPerson[]>("/current/contacts", config);
}

export function inviteContacts(people: ThinPerson[], config?: AxiosRequestConfig) {
  return client.post<null>("/current/contacts/invite", people, config);
}

export function authRedirect(
  provider: string,
  user?: User | null,
  state?: { [key: string]: string | null | number } | null
) {
  // FIXME: (IW) Doesn't work when base uri is just a path (eg. '/oauth/login'),
  // but window.location is no bueno
  const authUrl = new URL(`${LOGIN_BASE_URI}/${provider}`, window.location.href);
  if (state) authUrl.searchParams.append("state", JSON.stringify(state));

  if (!user) {
    authUrl.searchParams.append("prompt", "select_account");
  } else if (user.email && !user.impersonated) {
    authUrl.searchParams.append("login_hint", user.email);
  }

  window.location.href = authUrl.toString();
}

export function logout() {
  const url = new URL(LOGOUT_URI, window.location.href);
  window.location.href = url.toString();
}

export default {
  getCurrentUser,
  updateCurrentUser,
  deleteCurrentUser,
  getCurrentUserContacts,
  inviteContacts,
  logout,
};
